/**
 *
 * Why is there an input?
 * If the slider is used in a form we'll need an input field to capture the value.
 * We'll assume this when the component is given a form field name (A `name` prop
 * doesn't really make sense in any other context).
 *
 * Helpful links:
 * https://github.com/reach/reach-ui/blob/7059b23e12546796a054704c38983e3e422ef506/packages/slider/src/index.tsx
 * https://www.w3.org/TR/wai-aria-practices-1.2/#slider_roles_states_props
 * https://www.w3.org/TR/wai-aria-practices-1.2/#slider_kbd_interaction
 */

import { faMapMarker } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
import { isRightClick, noop } from '@modules/helpers';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from './Slider.module.scss';

type SliderProps = {
  min: number;
  max: number;
  value: number;
  disabled?: boolean;
  marks?: boolean;
  resetValue?: number | undefined;
  numSteps?: number;
  valueLabelDisplay?: 'auto' | 'on';
  name?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  'aria-valuetext'?: string;
  onValueChange: (newValue: number) => void;
  onFocus?: (isFocused: boolean) => void;
};

interface PointerPosition {
  x: number;
  y: number;
}

function getLargeStep(numSteps: number) {
  if (numSteps <= 8) return numSteps;
  else return Math.floor(Math.sqrt(numSteps));
}

function getPointerPosition(
  event: MouseEvent | PointerEvent | TouchEvent,
  touchId: number | undefined,
): PointerPosition | undefined {
  if (window.TouchEvent && event instanceof TouchEvent) {
    if (touchId !== undefined && event.changedTouches) {
      for (let i = 0; i < event.changedTouches.length; i += 1) {
        const touch = event.changedTouches[i];
        if (touch.identifier === touchId) {
          return {
            x: touch.clientX,
            y: touch.clientY,
          };
        }
      }
    }

    return undefined;
  }

  return {
    x: (event as MouseEvent).clientX,
    y: (event as MouseEvent).clientY,
  };
}

function getNewValue(
  pointerPosition: PointerPosition | undefined,
  railEl: HTMLElement | undefined,
  props: Pick<SliderProps, 'min' | 'max'> & { numSteps: number },
) {
  if (!railEl || !pointerPosition) return;

  const { min, max, numSteps } = props;
  const { left, width } = railEl.getBoundingClientRect();
  const distanceFromStart = pointerPosition.x - left;
  const range = max - min;
  const newUnroundedValue = min + (distanceFromStart / width) * range;
  const stepSize = range / (numSteps - 1);

  const newValue = min + Math.round((newUnroundedValue - min) / stepSize) * stepSize;

  return newValue;
}

const Slider: React.FC<React.PropsWithChildren<SliderProps>> = (props) => {
  const {
    min,
    max,
    value,
    disabled = false,
    marks = false,
    resetValue = undefined,
    numSteps = 100,
    valueLabelDisplay,
    'aria-labelledby': labelledBy,
    'aria-label': ariaLabel,
    'aria-valuetext': ariaValueText,
    onValueChange,
    onFocus,
  } = props;

  if (resetValue !== undefined) {
    if (resetValue > max || resetValue < min) {
      throw new Error('Reset Marker must not be outside min-max range');
    }
  }

  const pointerDownRef = useRef(false);
  const touchIdRef = useRef<number>(undefined);
  const [sliderNode, sliderRef] = useNode<HTMLElement>();
  const [railNode, railRef] = useNode<HTMLElement>();
  const onValueChangeRef = useRef(onValueChange);
  const [hasFocus, setHasFocus] = useState(false);

  const removeMoveEvents = useRef<() => void>(noop);
  const removeStartEvents = useRef<() => void>(noop);
  const removeEndEvents = useRef<() => void>(noop);

  const range = max - min;
  const trackWidth = ((value - min) / range) * 100;
  const resetPosition = resetValue !== undefined ? ((resetValue - min) / range) * 100 : undefined;

  useEffect(() => {
    onFocus?.(hasFocus);
  }, [onFocus, hasFocus]);

  useEffect(() => {
    onValueChangeRef.current = onValueChange;
  }, [onValueChange]);

  const handlePointerMove = useCallback(
    (event: TouchEvent | MouseEvent) => {
      if (disabled || !pointerDownRef.current) {
        pointerDownRef.current = false;
        return;
      }

      const pointerPosition = getPointerPosition(event, touchIdRef.current);
      let newValue = getNewValue(pointerPosition, railNode, { min, max, numSteps });

      if (newValue == null) {
        return;
      }

      newValue = Math.min(Math.max(newValue, min), max);
      onValueChangeRef.current(newValue);
    },
    [disabled, max, min, numSteps, railNode],
  );

  const setPointerCapture = useCallback(
    (event: PointerEvent) => {
      if (isRightClick(event)) return;
      if (disabled) {
        pointerDownRef.current = false;
        return;
      }
      setHasFocus(true);
      pointerDownRef.current = true;
      railNode?.setPointerCapture(event.pointerId);
    },
    [disabled, railNode],
  );

  const releasePointerCapture = useCallback(
    (event: PointerEvent) => {
      if (isRightClick(event)) return;
      setHasFocus(false);
      railNode?.releasePointerCapture(event.pointerId);
      pointerDownRef.current = false;
    },
    [railNode],
  );

  const handleSlideStop = useCallback(
    (event: TouchEvent | MouseEvent) => {
      if (isRightClick(event)) return;

      pointerDownRef.current = false;

      setHasFocus(false);

      const pointerPosition = getPointerPosition(event, touchIdRef.current);
      const newValue = getNewValue(pointerPosition, railNode, { min, max, numSteps });

      if (newValue == null) {
        return;
      }

      touchIdRef.current = undefined;

      removeMoveEvents.current();
      removeEndEvents.current();
    },
    [max, min, numSteps, railNode],
  );

  const addMoveListener = useCallback(() => {
    const ownerDocument = railNode?.ownerDocument || window.document;
    const touchListener = handlePointerMove;
    const mouseListener = handlePointerMove;
    ownerDocument.addEventListener('touchmove', touchListener);
    ownerDocument.addEventListener('mousemove', mouseListener);
    return () => {
      ownerDocument.removeEventListener('touchmove', touchListener);
      ownerDocument.removeEventListener('mousemove', mouseListener);
    };
  }, [handlePointerMove, railNode?.ownerDocument]);

  const addEndListener = useCallback(() => {
    const ownerDocument = railNode?.ownerDocument || window.document;
    const ownerWindow = ownerDocument.defaultView || window;
    const pointerListener = releasePointerCapture;
    const touchListener = handleSlideStop;
    const mouseListener = handleSlideStop;

    if ('PointerEvent' in ownerWindow) {
      ownerDocument.addEventListener('pointerup', pointerListener);
    }
    ownerDocument.addEventListener('touchend', touchListener);
    ownerDocument.addEventListener('mouseup', mouseListener);

    return () => {
      if ('PointerEvent' in ownerWindow) {
        ownerDocument.removeEventListener('pointerup', pointerListener);
      }
      ownerDocument.removeEventListener('touchend', touchListener);
      ownerDocument.removeEventListener('mouseup', mouseListener);
    };
  }, [handleSlideStop, railNode?.ownerDocument, releasePointerCapture]);

  const handleSlideStart = useCallback(
    (event: TouchEvent | MouseEvent) => {
      if (isRightClick(event)) return;

      if (disabled) {
        pointerDownRef.current = false;
        return;
      }

      setHasFocus(true);

      if (window.TouchEvent && event instanceof TouchEvent) {
        // Prevent scrolling for touch events
        event.preventDefault();
        const touch = event.changedTouches?.[0];
        if (touch != null) touchIdRef.current = touch.identifier;
      }

      const pointerPosition = getPointerPosition(event, touchIdRef.current);
      const newValue = getNewValue(pointerPosition, railNode, { min, max, numSteps });

      if (!newValue) return;
      onValueChangeRef.current(newValue);

      removeMoveEvents.current = addMoveListener();
      removeEndEvents.current = addEndListener();
    },
    [addEndListener, addMoveListener, disabled, max, min, numSteps, railNode],
  );

  const addStartListener = useCallback(() => {
    // e.preventDefault is ignored by React's synthetic touchStart event, so
    // we attach the listener directly to the DOM node
    // https://github.com/facebook/react/issues/9809#issuecomment-413978405
    if (!sliderNode) return noop;

    const ownerDocument = sliderNode?.ownerDocument || window.document;
    const ownerWindow = ownerDocument.defaultView || window;
    const touchListener = handleSlideStart;
    const mouseListener = handleSlideStart;
    const pointerListener = setPointerCapture;
    sliderNode.addEventListener('touchstart', touchListener);
    sliderNode.addEventListener('mousedown', mouseListener);
    if ('PointerEvent' in ownerWindow) {
      sliderNode.addEventListener('pointerdown', pointerListener);
    }
    return () => {
      sliderNode.removeEventListener('touchstart', touchListener);
      sliderNode.removeEventListener('mousedown', mouseListener);
      if ('PointerEvent' in ownerWindow) {
        sliderNode.removeEventListener('pointerdown', pointerListener);
      }
    };
  }, [setPointerCapture, handleSlideStart, sliderNode]);

  useEffect(() => {
    removeStartEvents.current = addStartListener();

    return () => {
      removeStartEvents.current();
      removeEndEvents.current();
      removeMoveEvents.current();
    };
  }, [addStartListener]);

  function handleKeyDown(event: React.KeyboardEvent) {
    if (disabled) return;

    let newValue: number;
    const largeStep = range / (getLargeStep(numSteps) - 1);
    const keyStep = range / (numSteps - 1);

    switch (event.key) {
      // Decrease the value of the slider by one step.
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = value - keyStep;
        break;
      // Increase the value of the slider by one step
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = value + keyStep;
        break;
      // Decrement the slider by an amount larger than the step change made by
      // `ArrowDown`.
      case 'PageDown':
        newValue = value - largeStep;
        break;
      // Increment the slider by an amount larger than the step change made by
      // `ArrowUp`.
      case 'PageUp':
        newValue = value + largeStep;
        break;
      // Set the slider to the first allowed value in its range.
      case 'Home':
        newValue = min;
        break;
      // Set the slider to the last allowed value in its range.
      case 'End':
        newValue = max;
        break;
      default:
        return;
    }

    // If one of the keys we care about was pressed prevent the default behaviour
    event.preventDefault();

    newValue = Math.min(Math.max(newValue, min), max);
    onValueChangeRef.current(newValue);
  }

  const handleReset = () => {
    if (resetValue !== undefined) {
      onValueChangeRef.current(resetValue);
    }
  };

  const showResetMarker = useMemo(() => {
    if (resetPosition !== undefined) {
      return Math.abs(trackWidth - resetPosition) > 10;
    } else {
      return false;
    }
  }, [resetPosition, trackWidth]);

  return (
    <div className={styles['slider-container']}>
      {resetPosition !== undefined && (
        <ResetMarker show={showResetMarker} onReset={handleReset} resetPosition={resetPosition} />
      )}
      <div className={styles['slider']} data-disabled={disabled} ref={sliderRef}>
        <span className={styles['rail']} ref={railRef}></span>
        <span className={styles['track']} style={{ width: `${trackWidth}%` }}></span>
        <input type="hidden" value={value} name={props.name} />
        {marks && <Marks numMarks={numSteps} value={value} min={min} max={max} />}

        <span
          className={styles['thumb']}
          tabIndex={disabled ? -1 : 0}
          role="slider"
          aria-label={labelledBy}
          aria-labelledby={ariaLabel ? undefined : labelledBy}
          aria-valuetext={ariaValueText}
          aria-orientation="horizontal"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          onKeyDown={handleKeyDown}
          style={{ left: `${trackWidth}%` }}
          data-has-focus={hasFocus}
          onBlur={() => {
            setHasFocus(false);
          }}
          onFocus={() => {
            setHasFocus(true);
          }}
        >
          {valueLabelDisplay && (
            <span className={styles['value-label']} data-mode={valueLabelDisplay}>
              <span className={styles['value-label-shape']}>
                <span className={styles['value-label-value']}>{value.toFixed(1)}</span>
              </span>
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

interface MarksProps {
  numMarks: number;
  value: number;
  min: number;
  max: number;
}

const Marks: React.FC<React.PropsWithChildren<MarksProps>> = ({ numMarks, value, min, max }) => {
  if (numMarks < 2) throw new Error('numMarks must be 2 or more');
  const range = max - min;
  const keyStep = range / numMarks;

  return (
    <>
      {Array(numMarks)
        .fill(undefined)
        .map((v, i) => (
          <span
            className={styles['mark']}
            style={{ '--index': i, left: `${(100 / (numMarks - 1)) * i}%` }}
            data-is-active={min + keyStep * i < value}
            // biome-ignore lint/suspicious/noArrayIndexKey: historic
            key={i}
          />
        ))}
    </>
  );
};

interface ResetMarkerProps {
  resetPosition: number;
  onReset: () => void;
  show: boolean;
}
const ResetMarker: React.FC<React.PropsWithChildren<ResetMarkerProps>> = ({
  resetPosition,
  onReset,
  show,
}) => {
  const handleReset: React.MouseEventHandler<HTMLSpanElement> = (e) => {
    if (show) {
      e.preventDefault();
      onReset();
    }
  };
  const handleKeyPress: React.KeyboardEventHandler<HTMLSpanElement> = (e) => {
    if (show) {
      e.preventDefault();
      onReset();
    }
  };
  return (
    <span
      tabIndex={0}
      onKeyPress={handleKeyPress}
      className={styles['reset-marker']}
      style={{ left: `${resetPosition}%` }}
      onClick={handleReset}
      role="button"
    >
      {show && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <FontAwesomeIcon icon={faMapMarker} size="lg" />
        </motion.div>
      )}
    </span>
  );
};

export default Slider;
